// ============================================================================
// API — Comprobantes (panel-cliente).
//
// Subir/eliminar comprobantes de pago SIEMPRE a través de la Edge Function
// `subir_comprobante` (usa service_role). El bucket `comprobantes` es PRIVADO
// y NO tiene policy INSERT para authenticated; el navegador jamás debe llamar
// a storage.from(...).upload(...) ni a getPublicUrl.
//
// `subirComprobante` lee el archivo en base64 (data URI) y lo envía al edge;
// devuelve el PATH físico `{empresaId}/{uuid}{ext}` (NO una URL pública),
// que luego se guarda en comprobante_url / evidencia_url.
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';

function mimeDesdeNombre(nombre) {
  const extension = String(nombre ?? '')
    .split('.')
    .pop()
    ?.toLowerCase();
  const mimePorExtension = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    pdf: 'application/pdf',
  };
  return mimePorExtension[extension] ?? '';
}

/**
 * Lee un File como data URI (base64).
 * @param {File} archivo
 * @returns {Promise<string>}
 */
function leerComoDataUrl(archivo) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(archivo);
  });
}

/**
 * Sube un comprobante al bucket privado vía Edge Function (service_role).
 * El cliente nunca escribe directo en Storage.
 *
 * @param {File} archivo - imagén o PDF.
 * @returns {Promise<{ruta: string|null, error: object|null}>}
 *   `ruta` es el PATH físico `{empresaId}/{uuid}{ext}` a guardar en
 *   comprobante_url / evidencia_url (no es una URL navegable).
 */
export async function subirComprobante(archivo) {
  if (!archivo) {
    return { ruta: null, error: { message: 'Seleccione un comprobante.' } };
  }

  const TAIL = 5 * 1024 * 1024;
  if (archivo.size > TAIL) {
    return { ruta: null, error: { message: 'El archivo supera los 5 MB.' } };
  }

  let base64 = '';
  try {
    base64 = await leerComoDataUrl(archivo);
  } catch (error) {
    return {
      ruta: null,
      error: error instanceof Error ? error : new Error('No se pudo leer el archivo.'),
    };
  }

  if (!base64) {
    return { ruta: null, error: { message: 'No se pudo leer el archivo.' } };
  }

  const { data, error } = await supabaseClient().functions.invoke('subir_comprobante', {
    body: {
      accion: 'subir',
      nombre: archivo.name,
      // En Android, File.type suele venir vacío aunque el nombre conserve la
      // extensión. La Edge Function exige MIME permitido, así que derivarlo.
      mime: archivo.type || mimeDesdeNombre(archivo.name) || 'application/octet-stream',
      base64,
    },
  });

  if (error) {
    return { ruta: null, error };
  }

  const ruta = data?.ruta ?? null;
  if (!ruta) {
    return { ruta: null, error: { message: data?.error ?? 'No se pudo subir el comprobante.' } };
  }

  return { ruta, error: null };
}

/**
 * Elimina un comprobante previamente subido (limpieza best-effort ante fallo).
 * Solo puede borrar objetos de su propia empresa (lo valida la Edge Function).
 *
 * @param {string} ruta - PATH devuelto por subirComprobante.
 * @returns {Promise<{error: object|null}>}
 */
export async function eliminarComprobante(ruta) {
  if (!ruta) {
    return { error: null };
  }

  const { error } = await supabaseClient().functions.invoke('subir_comprobante', {
    body: { accion: 'borrar', ruta },
  });

  if (error) {
    return { error };
  }

  return { error: null };
}
